/**
 * @fileoverview `useFloatingPosition` — curated wrapper around
 * `@floating-ui/react`'s `useFloating` hook.
 *
 * Bakes in the ecosystem-wide defaults for floating elements:
 *   - `offset(8)` — 8px gap between trigger and floating element
 *   - `flip()` — flip to opposite side when there's no room
 *   - `shift({ padding: 8 })` — slide along the cross-axis to stay
 *     in the viewport (8px viewport padding)
 *   - `autoUpdate` — re-measure on scroll / resize / DOM changes
 *
 * Plus opt-in dismiss behavior:
 *   - Escape key dismisses (default `true`)
 *   - Outside click / pointer-down dismisses (default `true`)
 *
 * Plus opt-in arrow positioning:
 *   - When `hasArrow=true` and an `arrowRef` is provided, the
 *     `arrow()` middleware runs and `middlewareData.arrow` carries
 *     the {x, y} offset the consumer applies to its arrow element.
 *
 * Plus opt-in in-floating roving keyboard navigation:
 *   - When a `listNavigation` descriptor is supplied, the hook
 *     registers floating-ui's `useListNavigation` interaction so
 *     Arrow / Home / End move a roving DOM focus across the registered
 *     item refs (default OFF — the descriptor's absence leaves the
 *     interaction disabled). The descriptor carries the floating-ui
 *     essentials (`listRef`, `activeIndex`, `onNavigate`) plus the
 *     relevant flags (`loop`, `focusItemOnOpen`, `virtual`,
 *     `selectedIndex`, `nested`, `orientation`). The hook surfaces
 *     `getItemProps` for the consumer to spread onto each item element.
 *     Menu + listbox consumers stop hand-rolling `focusedIdx` + a
 *     `document` keydown listener once they opt in.
 *
 * The single-source-of-truth for floating-element positioning across
 * the ecosystem. `tooltip`, `popover`, `command-palette`, dropdown
 * menus, and the like all consume this hook. Direct imports of
 * `@floating-ui/react` outside this package are forbidden by the
 * `web/no-local-floating-ui` ESLint rule.
 *
 * @module @puffin-core/floating-ui-base/useFloatingPosition
 * @see ~/.claude/plans/distributed-toasting-kernighan.md (Phase 1)
 */

import { useRef } from 'react';
import {
  useFloating,
  useClick,
  useDismiss,
  useListNavigation,
  useInteractions,
  autoUpdate,
  offset as offsetMiddleware,
  flip,
  shift,
  size as sizeMiddleware,
  arrow as arrowMiddleware,
} from '@floating-ui/react';

const DEFAULT_PLACEMENT = 'bottom';
const DEFAULT_OFFSET_PX = 8;
const DEFAULT_VIEWPORT_PADDING_PX = 8;
const DEFAULT_STRATEGY = 'absolute';
// The CSS custom property the `size()` middleware writes with the height the
// floating element has available before it would overflow the viewport. CSS in
// shared-styles owns the `max-height`/`overflow-y` declarations and reads this
// variable — the factory injects only the dynamic scalar (the same sanctioned
// "factory-owned dynamic write" class as `floatingStyles`' transform).
const AVAILABLE_HEIGHT_VAR = '--available-height';

/**
 * Curated wrapper around `@floating-ui/react`'s `useFloating`.
 *
 * @param {Object} [options]
 * @param {boolean} [options.open] - Controlled open state. Optional
 *   for non-interactive usage (e.g. always-visible labels).
 * @param {(open: boolean) => void} [options.onOpenChange] - Called
 *   when a dismiss interaction (Escape / outside-click) requests
 *   closing. Required if `dismissOnEscape` or `dismissOnOutsideClick`
 *   is enabled.
 * @param {string} [options.placement='bottom'] - Floating-UI
 *   placement string (e.g. `'top'`, `'right-start'`).
 * @param {('absolute'|'fixed')} [options.strategy='absolute'] - The
 *   CSS positioning strategy floating-ui writes into `floatingStyles`.
 *   `'absolute'` (default) positions relative to the nearest
 *   positioned ancestor — fine for portaled or top-level floating
 *   elements. `'fixed'` positions relative to the viewport, which
 *   escapes any `overflow: auto | hidden` clipping ancestor chain
 *   (the DRP-001 requirement for dropdowns rendered in-flow inside
 *   scroll/border-radius containers). With `autoUpdate` the fixed
 *   element re-anchors to its trigger on scroll/resize instead of
 *   detaching.
 * @param {number} [options.offsetPx=8] - Pixel gap between trigger
 *   and floating element. Applied via the `offset()` middleware.
 * @param {boolean} [options.hasArrow=false] - Enable the `arrow()`
 *   middleware for caret-style positioning. Requires `arrowRef`.
 * @param {React.MutableRefObject<HTMLElement|null>} [options.arrowRef]
 *   - Ref to the consumer's arrow element. Required when
 *   `hasArrow=true`.
 * @param {boolean} [options.fitViewport=false] - Opt IN to viewport-fit
 *   height. When `true`, the `size()` middleware runs and writes the
 *   height the floating element has available before it would overflow
 *   the viewport into the `--available-height` CSS custom property on
 *   the floating node. Shared CSS reads that variable
 *   (`max-height: var(--available-height); overflow-y: auto`, with the
 *   pre-set default DECLARED on the consuming rule since
 *   CSS-FALLBACK-STRIP-001) so a
 *   tall panel caps to the viewport and scrolls internally instead of
 *   being clipped. Default OFF so every existing consumer is
 *   byte-unaffected (the middleware is simply absent).
 * @param {boolean} [options.dismissOnEscape=true] - Dismiss on
 *   `Escape` keypress.
 * @param {boolean} [options.dismissOnOutsideClick=true] - Dismiss
 *   on outside pointer-down.
 * @param {boolean} [options.enableClick=false] - Opt IN to the
 *   open-on-click toggle (floating-ui `useClick`). OFF by default so
 *   the base hook stays interaction-agnostic for hover surfaces
 *   (tooltips). `usePopover` sets this true; consumers that enable it
 *   must NOT also hand-roll an onClick on the trigger (it double-fires).
 * @param {Object|null} [options.listNavigation=null] - Opt IN to
 *   in-floating roving keyboard navigation (floating-ui
 *   `useListNavigation`). Default OFF (`null` / omitted → the
 *   interaction is registered `disabled` so existing consumers are
 *   byte-unaffected). When supplied, the descriptor's fields are
 *   forwarded to `useListNavigation`:
 *   @param {React.MutableRefObject<Array<HTMLElement|null>>}
 *     options.listNavigation.listRef - REQUIRED. Mutable ref whose
 *     `.current` is the array of item element refs (index-aligned with
 *     the rendered items). Arrow / Home / End move focus across these.
 *   @param {number|null} options.listNavigation.activeIndex - REQUIRED.
 *     The controlled active (roving-focus) index. `null` = no active
 *     item. The consumer owns this state and updates it from
 *     `onNavigate`.
 *   @param {(index: number|null) => void} options.listNavigation.onNavigate -
 *     REQUIRED. Called by floating-ui when navigation moves the active
 *     index. The consumer mirrors it into its `activeIndex` state.
 *   @param {boolean} [options.listNavigation.loop=false] - Wrap from
 *     last↔first (menu semantics). Off = clamp at the ends.
 *   @param {boolean|'auto'} [options.listNavigation.focusItemOnOpen='auto'] -
 *     Whether to focus an item when the floating element opens.
 *   @param {boolean} [options.listNavigation.virtual=false] - Listbox /
 *     combobox mode: focus stays on the reference (e.g. a search input)
 *     and the active item is advertised via `aria-activedescendant`
 *     instead of receiving real DOM focus.
 *   @param {number|null} [options.listNavigation.selectedIndex=null] -
 *     The currently-selected index (listbox), used as the landing spot
 *     when the list opens.
 *   @param {boolean} [options.listNavigation.nested=false] - Submenu mode.
 *   @param {('vertical'|'horizontal'|'both')} [options.listNavigation.orientation='vertical'] -
 *     Arrow-key axis.
 *   @param {boolean} [options.listNavigation.enabled=true] - Per-call
 *     gate (in addition to the descriptor presence). Pass `false` to
 *     keep the interaction registered but inert.
 * @returns {{
 *   refs: ReturnType<typeof useFloating>['refs'],
 *   floatingStyles: React.CSSProperties,
 *   context: ReturnType<typeof useFloating>['context'],
 *   middlewareData: ReturnType<typeof useFloating>['middlewareData'],
 *   placement: string,
 *   getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'],
 *   getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'],
 *   getItemProps: ReturnType<typeof useInteractions>['getItemProps'],
 * }}
 */
export function useFloatingPosition(options = {}) {
  const {
    open,
    onOpenChange,
    placement = DEFAULT_PLACEMENT,
    strategy = DEFAULT_STRATEGY,
    offsetPx = DEFAULT_OFFSET_PX,
    hasArrow = false,
    arrowRef = null,
    fitViewport = false,
    dismissOnEscape = true,
    dismissOnOutsideClick = true,
    // Opt-IN open-on-click toggle. The base hook is interaction-agnostic — it
    // provides positioning + dismiss for ALL floating surfaces, popovers AND
    // tooltips. `usePopover` opts IN (so popovers own their click toggle);
    // `useTooltip` leaves it OFF (hover-only) by relying on this default. When
    // ON, the consumer must NOT also hand-roll an onClick (it double-fires).
    enableClick = false,
    // Opt-IN in-floating roving keyboard navigation. Default `null` (OFF) so
    // every existing consumer is byte-unaffected: the interaction is still
    // registered (floating-ui requires a stable interactions array between
    // renders) but `enabled: false`, so it adds no behavior. When a descriptor
    // is supplied, Arrow/Home/End move a roving focus across the item refs.
    listNavigation = null,
  } = options;

  const middleware = [
    offsetMiddleware(offsetPx),
    flip(),
    shift({ padding: DEFAULT_VIEWPORT_PADDING_PX }),
  ];
  // Viewport-fit height (opt-in). Ordered after flip()/shift() so it measures
  // the space left in the RESOLVED placement. `size()` surfaces the available
  // height only through its `apply` callback (never `middlewareData`), so the
  // factory consumes it here and writes it to a CSS variable the stylesheet
  // reads — keeping the max-height/overflow declarations token-driven + auditable
  // in shared-styles rather than as an un-overridable inline style.
  if (fitViewport) {
    middleware.push(
      sizeMiddleware({
        padding: DEFAULT_VIEWPORT_PADDING_PX,
        apply({ availableHeight, elements }) {
          elements.floating.style.setProperty(
            AVAILABLE_HEIGHT_VAR,
            `${Math.max(0, Math.round(availableHeight))}px`,
          );
        },
      }),
    );
  }
  if (hasArrow && arrowRef) {
    middleware.push(arrowMiddleware({ element: arrowRef }));
  }

  const floating = useFloating({
    open,
    onOpenChange,
    placement,
    strategy,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  // CRITICAL: `useClick` makes the factory own the open-on-click toggle. Without
  // it `getReferenceProps()` returns no onClick and the popover never opens on a
  // trigger click unless the consumer hand-rolls one (the historical UAT-185
  // workaround in NavKebabMenu/NavFlyout that left MatrixAssignPopover dead).
  // The trigger's aria-expanded/-haspopup is owned one layer up in `usePopover`.
  const click = useClick(floating.context, { enabled: enableClick });
  const dismiss = useDismiss(floating.context, {
    escapeKey: dismissOnEscape,
    outsidePress: dismissOnOutsideClick,
  });

  // Roving keyboard navigation. `useListNavigation` is a hook, so it must be
  // called unconditionally on every render (Rules of Hooks). When the consumer
  // opts OUT (`listNavigation === null`) we register it `disabled` with a stable
  // fallback `listRef` so the interactions array shape never changes between
  // renders — that keeps the OFF path byte-equivalent to the pre-listNav hook.
  const fallbackListRef = useRef([]);
  const listNav = useListNavigation(floating.context, {
    enabled: Boolean(listNavigation) && listNavigation.enabled !== false,
    listRef: listNavigation ? listNavigation.listRef : fallbackListRef,
    activeIndex: listNavigation ? listNavigation.activeIndex : null,
    onNavigate: listNavigation ? listNavigation.onNavigate : undefined,
    loop: listNavigation ? Boolean(listNavigation.loop) : false,
    focusItemOnOpen: listNavigation ? (listNavigation.focusItemOnOpen ?? 'auto') : 'auto',
    virtual: listNavigation ? Boolean(listNavigation.virtual) : false,
    selectedIndex: listNavigation ? (listNavigation.selectedIndex ?? null) : null,
    nested: listNavigation ? Boolean(listNavigation.nested) : false,
    orientation: listNavigation ? (listNavigation.orientation ?? 'vertical') : 'vertical',
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    listNav,
  ]);

  return {
    refs: floating.refs,
    floatingStyles: floating.floatingStyles,
    context: floating.context,
    middlewareData: floating.middlewareData,
    placement: floating.placement,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  };
}
